import {calculateErrorMessage} from 'common/helpers'
import {WAVRecorder} from 'common/helpers/wavRecorder'
import {useCallback, useEffect, useRef, useState} from 'react'
import {transcribeVoiceRecordingDetailed} from 'setup/redux/Actions/Chatbot/chatbot'

export type DictationStatus = 'idle' | 'recording' | 'transcribing' | 'error'

/**
 * Azure's short-audio REST endpoint — what `chatbot/voice/transcribe` calls — rejects recordings
 * longer than a minute, so the recorder stops itself before the user can produce one that will be
 * thrown away. Stopping transcribes what was captured; it is not an error.
 */
const MAX_RECORDING_MS = 55_000

/**
 * A recording with less speech than this is not uploaded: silence, a cough or room noise sent to a
 * recogniser comes back as invented text. The server applies the same gate (node-api
 * speech/audioActivity.js); checking here too saves the round trip.
 */
const MIN_ACTIVE_SPEECH_MS = 400

/** What the server knows about a transcript, beyond its text. */
export interface DictationTranscriptMeta {
  /** 0..1, or null when the recogniser does not score its output. */
  confidence: number | null
  /**
   * THE SERVER'S OWN VERDICT on that confidence — flag this transcript for the
   * user to read before sending?
   *
   * Not a threshold applied here. The composer used to compare `confidence`
   * against a constant of its own, which meant two places had to agree on what
   * "low" means and on what an unscored transcript (confidence null) counts as.
   * The endpoint decides; every client gets the same answer.
   */
  lowConfidence: boolean
  /**
   * What was SPOKEN, as the endpoint determined it: 'ur' | 'ar' | 'en' | 'fr' |
   * 'tr'. Never re-derive this from the text — by the time it arrives, Urdu and
   * Arabic have been rewritten in Latin letters and look like the rest.
   */
  detectedLanguage: string | null
  transcriptId: string | null
}

export interface UseVoiceDictationOptions {
  /** Receives the server's transcription. Not called when the recording is cancelled or silent. */
  onTranscribed?: (text: string, meta: DictationTranscriptMeta) => void
  /** Receives a translation key, so the caller decides how to surface it. */
  onError?: (errorKey: string) => void
}

export interface UseVoiceDictationReturn {
  status: DictationStatus
  isRecording: boolean
  isTranscribing: boolean
  isSupported: boolean
  errorKey: string | null
  start: () => Promise<void>
  stop: () => Promise<void>
  cancel: () => void
}

/**
 * Dictation by recording first and transcribing afterwards.
 *
 * The whole utterance is sent to the server once the user stops speaking, rather than being decoded
 * live in the browser. The server sees all the audio at once and detects the spoken language itself
 * — nothing about the language is sent to it at all — so it is not guessing from a partial sentence
 * in a language fixed up front, which is what made live transcription rewrite words mid-dictation and
 * change the meaning of Urdu speech.
 */
export const useVoiceDictation = (
  options: UseVoiceDictationOptions = {}
): UseVoiceDictationReturn => {
  const {onTranscribed, onError} = options

  const [status, setStatus] = useState<DictationStatus>('idle')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [isSupported, setIsSupported] = useState(true)

  const recorderRef = useRef<WAVRecorder | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const autoStopRef = useRef<number | null>(null)
  const cancelledRef = useRef(false)

  const callbacksRef = useRef({onTranscribed, onError})
  useEffect(() => {
    callbacksRef.current = {onTranscribed, onError}
  }, [onTranscribed, onError])

  const fail = useCallback((key: string) => {
    setErrorKey(key)
    setStatus('error')
    callbacksRef.current.onError?.(key)
    // Errors are transient UI state; the button returns to idle so the user can simply try again.
    window.setTimeout(() => setStatus((s) => (s === 'error' ? 'idle' : s)), 1500)
  }, [])

  const clearAutoStop = useCallback(() => {
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current)
      autoStopRef.current = null
    }
  }, [])

  const releaseRecorder = useCallback(() => {
    clearAutoStop()
    if (recorderRef.current) {
      try {
        recorderRef.current.cancel()
      } catch (e) {
        calculateErrorMessage(e)
      }
      recorderRef.current = null
    }
  }, [clearAutoStop])

  const stop = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder) return
    clearAutoStop()
    recorderRef.current = null
    cancelledRef.current = false

    let blob: Blob
    try {
      blob = await recorder.stop()
    } catch (e) {
      calculateErrorMessage(e)
      fail('CHAT.VOICE.ERROR.RECOGNITION_FAILED')
      return
    }

    if (!blob || blob.size === 0) {
      setStatus('idle')
      return
    }

    const stats = recorder.getLastRecordingStats()
    if (stats && stats.activeSpeechMs < MIN_ACTIVE_SPEECH_MS) {
      fail('CHAT.VOICE.ERROR.NO_SPEECH')
      return
    }

    setStatus('transcribing')
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const result = await transcribeVoiceRecordingDetailed(blob, {signal: controller.signal})
      if (cancelledRef.current || controller.signal.aborted) return

      setStatus('idle')
      if (!result.text) {
        fail('CHAT.VOICE.ERROR.NO_SPEECH')
        return
      }
      callbacksRef.current.onTranscribed?.(result.text, {
        confidence: result.confidence ?? null,
        lowConfidence: result.lowConfidence === true,
        detectedLanguage: result.detectedLanguage ?? null,
        transcriptId: result.transcriptId ?? null,
      })
    } catch (e) {
      if (cancelledRef.current || controller.signal.aborted) return
      calculateErrorMessage(e)
      // The server answers 400 "No speech detected ..." for silence, noise, or a transcript it
      // would not trust — that is "say it again", not "something broke".
      const message = (e as any)?.message
      fail(
        typeof message === 'string' && /no speech/i.test(message)
          ? 'CHAT.VOICE.ERROR.NO_SPEECH'
          : 'CHAT.VOICE.ERROR.TRANSCRIPTION_FAILED'
      )
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [clearAutoStop, fail])

  const start = useCallback(async () => {
    if (recorderRef.current) return

    setErrorKey(null)
    cancelledRef.current = false

    // getUserMedia (inside recorder.start) is what raises the browser's permission prompt
    // when the site has not decided yet — nothing is requested before this click.
    const recorder = new WAVRecorder()
    recorderRef.current = recorder

    try {
      await recorder.start()
    } catch (e) {
      calculateErrorMessage(e)
      recorderRef.current = null
      const name = (e as any)?.name
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        fail('CHAT.VOICE.ERROR.PERMISSION_DENIED')
      } else if (name === 'NotFoundError') {
        setIsSupported(false)
        fail('CHAT.VOICE.ERROR_NO_MICROPHONE')
      } else {
        fail('CHAT.VOICE.ERROR.FAILED_TO_START')
      }
      return
    }

    setStatus('recording')
    autoStopRef.current = window.setTimeout(() => {
      autoStopRef.current = null
      void stop()
    }, MAX_RECORDING_MS)
  }, [fail, stop])

  /** Drops the recording and any transcription already in flight, without emitting text. */
  const cancel = useCallback(() => {
    cancelledRef.current = true
    releaseRecorder()
    abortRef.current?.abort()
    abortRef.current = null
    setErrorKey(null)
    setStatus('idle')
  }, [releaseRecorder])

  useEffect(() => {
    let mounted = true
    void WAVRecorder.isSupported().then((supported) => {
      if (mounted) setIsSupported(Boolean(supported))
    })
    return () => {
      mounted = false
    }
  }, [])

  // Releasing the microphone on unmount matters here: the recorder holds a live MediaStream, so a
  // closed chat panel would otherwise keep the mic indicator lit.
  useEffect(
    () => () => {
      cancelledRef.current = true
      releaseRecorder()
      abortRef.current?.abort()
    },
    [releaseRecorder]
  )

  return {
    status,
    isRecording: status === 'recording',
    isTranscribing: status === 'transcribing',
    isSupported,
    errorKey,
    start,
    stop,
    cancel,
  }
}
